import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { licensesService, LicenseListParams } from '@/services/licensesService'

const licenseKeys = {
  all: ['licenses'] as const,
  list: (paramsKey: (string | number | boolean)[]) => [...licenseKeys.all, 'list', ...paramsKey] as const,
  detail: (licenseId: string) => [...licenseKeys.all, 'detail', licenseId] as const,
}

export interface UseLicenseListParams extends LicenseListParams {}

export function useLicenseList(params: UseLicenseListParams) {
  const queryParams = useMemo(() => {
    const sanitized: LicenseListParams = {
      page: params.page ?? 1,
      perPage: params.perPage ?? 10,
    }

    if (params.userId) sanitized.userId = params.userId
    if (params.type) sanitized.type = params.type
    if (params.programmeId) sanitized.programmeId = params.programmeId
    if (params.status) sanitized.status = params.status

    return sanitized
  }, [params.page, params.perPage, params.userId, params.type, params.programmeId, params.status])

  const key = [
    queryParams.page ?? 1,
    queryParams.perPage ?? 10,
    queryParams.userId ?? '',
    queryParams.type ?? '',
    queryParams.programmeId ?? '',
    queryParams.status ?? '',
  ]

  return useQuery({
    queryKey: licenseKeys.list(key),
    queryFn: () => licensesService.getLicenses(queryParams),
    enabled: Boolean(queryParams.userId),
    placeholderData: (previousData) => previousData,
    staleTime: 60 * 1000,
  })
}

export function useLicense(licenseId: string | undefined) {
  return useQuery({
    queryKey: licenseId ? licenseKeys.detail(licenseId) : licenseKeys.all,
    queryFn: () => (licenseId ? licensesService.getLicenseById(licenseId) : Promise.reject(new Error('License ID is required'))),
    enabled: Boolean(licenseId),
    staleTime: 60 * 1000,
  })
}
